"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jodit_min_js_1 = require("@/lib/jodit.min.js");
class FkTextEditor {
    selector;
    editor;
    options;
    constructor(selector, options) {
        this.selector = selector;
        this.options = this.#editorOptions(options);
        this.editor = jodit_min_js_1.Jodit.make(this.selector, this.options);
    }
    #editorOptions(options) {
        let defaultOptions = {
            uploader: {
                insertImageAsBase64URI: true,
            },
            filebrowser: {
                enabled: true,
            },
            style: {
                textColor: {
                    inline: true,
                },
            },
            imageDefaultWidth: null,
            showCharsCounter: false,
            showWordsCounter: false,
            showXPathInStatusbar: false,
            askBeforePasteHTML: false,
            askBeforePasteFromWord: false,
            defaultActionOnPaste: 'insert_html',
            toolbarAdaptive: false,
            cleanHTML: {
                removeEmptyBlocks: true
            },
            events: {
                afterInit: (editor) => {
                    $(document.body).on('click', '.jodit-toolbar-button[data-ref="brush"]', function (e) {
                        const target = e.target;
                        if (target?.classList) {
                            target.closest('.jodit-toolbar-button[data-ref="brush"]')?.classList.toggle('active');
                        }
                        const popupContainer = document.querySelectorAll('.jodit-popup__content');
                        if (popupContainer) {
                            popupContainer.forEach(container => {
                                const tabBtnWrapper = container.querySelector('.jodit-tabs__buttons');
                                if (tabBtnWrapper) {
                                    tabBtnWrapper.style.flexDirection = 'row-reverse';
                                    const tabBtn = tabBtnWrapper?.querySelector('button[data-ref="Text"]');
                                    tabBtn?.click();
                                }
                            });
                        }
                    });
                    editor.events.on('keydown', function (e) {
                        if (e.key === ' ' || e.key === 'Spacebar') {
                            const sel = editor.s;
                            const range = sel.range;
                            if (!range || !range.collapsed)
                                return;
                            const container = range.startContainer;
                            if (!container || container.nodeType !== Node.TEXT_NODE)
                                return;
                            const caretPos = range.startOffset;
                            const text = container.textContent;
                            const beforeCaret = text.slice(0, caretPos);
                            const match = beforeCaret.match(/^(\d+)\.(\d*\.*)*$/);
                            if (match) {
                                e.preventDefault();
                                const startNumber = parseInt(match[1], 10);
                                const numberText = match[0];
                                const afterCaret = text.slice(caretPos);
                                const remainingText = afterCaret.trimStart();
                                const list = document.createElement('ol');
                                if (startNumber > 1) {
                                    list.setAttribute('start', String(startNumber));
                                }
                                const listItem = document.createElement('li');
                                listItem.innerHTML = remainingText || '&nbsp;';
                                list.appendChild(listItem);
                                const parentBlock = container.parentElement;
                                if (parentBlock) {
                                    parentBlock.replaceWith(list);
                                }
                                sel.setCursorIn(listItem, 0);
                            }
                        }
                    });
                },
                beforePaste: async (e) => {
                    e.preventDefault();
                    const copiedData = e.clipboardData;
                    if (copiedData && copiedData.items) {
                        for (let i = 0; i < copiedData.items.length; i++) {
                            const item = copiedData.items[i];
                            if (item.type.startsWith('image/') && item.type == 'image/') {
                                const file = item.getAsFile();
                                if (file) {
                                    try {
                                        const reader = new FileReader();
                                        reader.onload = async (e) => {
                                            if (e.target) {
                                                const base64Image = e.target.result;
                                                let webpImage = await this.#convertImageToWebP(base64Image);
                                                this.editor.selection.insertImage(webpImage);
                                            }
                                        };
                                        reader.readAsDataURL(file);
                                    }
                                    catch (err) {
                                        console.log(err);
                                    }
                                }
                            }
                            else {
                                if (item.type === 'text/html') {
                                    e.preventDefault();
                                    let html = copiedData.getData('text/html');
                                    const tempDiv = document.createElement('div');
                                    tempDiv.innerHTML = html;
                                    let copiedItem = tempDiv.querySelectorAll('*');
                                    copiedItem.forEach(el => {
                                        el.style.fontSize = '';
                                        el.style.lineHeight = 'normal';
                                        el.style.fontFamily = '';
                                        el.style.color = '';
                                        el.style.backgroundColor = '';
                                        if (el.hasAttribute('contenteditable')) {
                                            el.removeAttribute('contenteditable');
                                        }
                                    });
                                    this.editor.selection.insertHTML(tempDiv.innerHTML);
                                }
                                else {
                                    e.preventDefault();
                                }
                            }
                        }
                    }
                },
                afterPaste: async (e) => {
                    const content = this.editor.getEditorValue();
                    if (/mso-/i.test(content) || /<o:p>/i.test(content)) {
                    }
                    else {
                        this.editor.execCommand('undo');
                    }
                },
                afterInsertImage: async (imgNode) => {
                    if (imgNode) {
                        const parentNode = imgNode.parentNode ? imgNode.parentNode : null;
                        const imagesInserted = parentNode?.querySelectorAll('img');
                        if (imagesInserted && imagesInserted.length > 1) {
                            imagesInserted.forEach((img, i) => {
                                if (i !== (imagesInserted.length - 1)) {
                                    img.remove();
                                }
                            });
                        }
                        else {
                            const base64ImgData = imgNode.src;
                            try {
                                let webpImage = await this.#convertImageToWebP(base64ImgData);
                                imgNode.src = webpImage;
                            }
                            catch (err) {
                                console.error(err);
                            }
                        }
                    }
                }
            },
            buttons: [
                {
                    name: 'source',
                    tooltip: 'Edit code'
                },
                'bold',
                'italic',
                'underline',
                {
                    name: 'strikethrough',
                    tooltip: 'Line through',
                },
                'brush',
                {
                    name: 'block',
                    tooltip: 'Format paragraph',
                    icon: 'paragraph',
                    list: {
                        'h5': 'Title',
                        'h6': 'Subtitle',
                        'p': 'Text',
                        'small': 'Small Text',
                        'i': 'Quote'
                    },
                    popup: true,
                    exec: (editor, current, control) => {
                        const tag = control.control.name;
                        if (tag === 'block') {
                            return false;
                        }
                        const sizeMap = {
                            h5: '28px',
                            h6: '22px',
                            p: '14px',
                            small: '13px',
                            i: '16px'
                        };
                        const fontSize = sizeMap[tag];
                        if (!fontSize)
                            return false;
                        const range = editor.s.range;
                        if (range && !range.collapsed) {
                            const selectedNode = editor.s.current();
                            if (selectedNode instanceof HTMLElement) {
                                selectedNode.style.fontSize = fontSize;
                            }
                            else {
                                editor.execCommand('fontSize', false, fontSize);
                            }
                        }
                        else {
                            const span = editor.createInside.element('span');
                            span.style.fontSize = fontSize;
                            editor.s.insertNode(span);
                            editor.s.setCursorIn(span, true);
                        }
                        editor.execCommand('formatBlock', false, tag);
                        editor.setEditorValue();
                        return false;
                    },
                },
                {
                    name: 'ul',
                    list: jodit_min_js_1.Jodit.atom(['inherit'])
                },
                {
                    name: 'ol',
                    list: jodit_min_js_1.Jodit.atom(['number'])
                },
                {
                    name: 'Line spacing',
                    tooltip: 'Format line',
                    icon: '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e8eaed"><path d="M240-160 80-320l56-56 64 62v-332l-64 62-56-56 160-160 160 160-56 56-64-62v332l64-62 56 56-160 160Zm240-40v-80h400v80H480Zm0-240v-80h400v80H480Zm0-240v-80h400v80H480Z"/></svg>',
                    list: {
                        '20px': '20px',
                        '25px': '25px',
                        '28px': '28px',
                        '30px': '30px',
                        '33px': '33px',
                        '37px': '37px'
                    },
                    exec: (editor, current, control) => {
                        const cmdName = control.control.name;
                        if (cmdName == "Line spacing") {
                            return false;
                        }
                        const range = editor.s.range;
                        if (range) {
                            const currentTag = range.startContainer.parentNode;
                            currentTag.style.lineHeight = cmdName;
                        }
                        return false;
                    },
                    isActive: (editor, control) => {
                        const selection = editor.s.range;
                        if (!selection)
                            return false;
                        const parentNode = selection.startContainer.parentNode;
                        const currentLineHeight = window.getComputedStyle(parentNode).lineHeight;
                        return Object.keys(control.control.list).includes(currentLineHeight);
                    }
                },
                'image',
                'link',
                'table',
                'align',
                ...(options?.buttons || []),
                'undo',
                'redo',
            ],
            controls: (options?.controls || {}),
        };
        if (options?.configBtn == false) {
            defaultOptions.buttons = options.buttons;
        }
        return defaultOptions;
    }
    #convertImageToWebP(base64Image, quality = 1) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = base64Image;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (ctx)
                    ctx.drawImage(img, 0, 0);
                const webpDataUrl = canvas.toDataURL('image/webp', quality);
                resolve(webpDataUrl);
            };
            img.onerror = (error) => reject(error);
        });
    }
}
exports.default = FkTextEditor;
